import React from 'react';

import { BillingManagementClaimsStepsRoutes } from './billingManagementClaimSteps';
import { Route } from 'helpers/routes/routes';
import { CommonPageData } from 'helpers/routes/CommonPageData';

export enum BillingManagementRoute {
  Billing = '/billing',
  AllInvoices = '/billing/all-invoices',
  OnlinePayment = '/billing/online-payment',
  OnlinePaymentConfirmation = '/billing/online-payment-confirm',
  DepositDetail = '/billing/deposit-detail',
  DebitDetail = '/billing/debit-detail',
  InvoiceDetail = '/billing/invoice-detail',
  PenaltyDetail = '/billing/penalty-detail',
  CreditDetail = '/billing/credit-detail',
  ProformaInvoiceDetail = '/billing/proforma-invoice-detail',
  BillingDetail = '/billing/:page(month-statement-detail|detail)',
  PaymentDetail = '/billing/payment-detail/:documentId/:caRefNo',
  PaymentsOverview = '/billing/payments-overview',
  Instalments = '/billing/installment-calendar-list',
  InstalmentDetail = '/billing/installment-detail/:id/:billingCaRefNo/:dueDate',
  InstalmentCalendarDetail = '/billing/installment-calendar-detail',
  InteractiveInvoiceServiceDetail = '/billing/interactive-invoice/:invoiceNumber/:serviceNo/:serviceId',
  TaxOverview = '/billing/tax/:invoiceNumber',
  ClaimsWizard = '/billing/claim',
  DetailList = '/billing/detail-statement-list/:invoiceNumber',
  DetailRecords = '/billing/detail-statement-items/:invoiceNumber/:serviceNo/:serviceId',
  BillsToPay = '/billing/billsToPay',
}

const defaultCommonPageData: CommonPageData = {
  pageTitle: 'onecrm.common.pageTitle.billing',
  visibleForAnonym: false,
};

export const BillingManagementRoutes: Route[] = [
  {
    path: BillingManagementRoute.Billing,
    exact: true,
    component: React.lazy(() => import('views/pages/Billing/List/List')),
    commonPageData: {
      ...defaultCommonPageData,
      pageId: '2.2.1',
    },
  },
  {
    path: BillingManagementRoute.AllInvoices,
    exact: true,
    component: React.lazy(() => import('views/pages/Billing/AllInvoices/AllInvoices')),
    commonPageData: {
      ...defaultCommonPageData,
      pageId: '2.2.3',
      pageTitle: 'onecrm.gui_billing.allInvoices.pageTitle',
    },
  },
  {
    path: BillingManagementRoute.PaymentsOverview,
    exact: true,
    component: React.lazy(() => import('views/pages/Billing/PaymentsOverview/PaymentsOverview')),
    commonPageData: {
      ...defaultCommonPageData,
      pageId: '2.2.1b',
    },
  },
  {
    path: BillingManagementRoute.Instalments,
    exact: true,
    component: React.lazy(() => import('views/pages/Billing/Instalments/Instalments')),
    commonPageData: {
      ...defaultCommonPageData,
      pageId: '2.2.1.1c',
    },
  },
  {
    path: BillingManagementRoute.InstalmentCalendarDetail,
    exact: true,
    component: React.lazy(() => import('views/pages/Billing/Instalments/InstalmentCalendarDetail')),
    commonPageData: {
      ...defaultCommonPageData,
      pageId: '2.2.1.2c',
      pageTitle: 'onecrm.gui_billing.installmentCalendarDetail.pageTitle',
    },
  },
  {
    path: BillingManagementRoute.InstalmentDetail,
    exact: true,
    component: React.lazy(() => import('views/pages/Billing/Instalments/InstalmentDetail')),
    commonPageData: {
      ...defaultCommonPageData,
      pageId: '2.2.1.7',
      pageTitle: 'onecrm.gui_billing.installmentDetail.pageTitle',
    },
  },
  {
    path: BillingManagementRoute.OnlinePaymentConfirmation,
    exact: true,
    component: React.lazy(() => import('views/pages/Billing/OnlinePayment/ConfirmOnlinePayment')),
    commonPageData: {
      ...defaultCommonPageData,
      pageId: '2.2.2.1.1',
    },
  },
  {
    path: BillingManagementRoute.OnlinePayment,
    exact: true,
    component: React.lazy(() => import('views/pages/Billing/OnlinePayment/OnlinePayment')),
    commonPageData: {
      ...defaultCommonPageData,
      pageId: '2.2.2.1',
      pageTitle: 'onecrm.gui_billing.onlinePayment.pageTitle',
    },
  },
  {
    path: BillingManagementRoute.InteractiveInvoiceServiceDetail,
    exact: true,
    component: React.lazy(() => import('views/pages/EBill/SummaryView/SummaryView')),
    commonPageData: {
      ...defaultCommonPageData,
      pageId: '2.2.1.1.1.1',
    },
  },
  {
    path: BillingManagementRoute.TaxOverview,
    exact: true,
    component: React.lazy(() => import('views/pages/EBill/TaxOverview/TaxOverview')),
    commonPageData: {
      ...defaultCommonPageData,
      pageId: '2.2.1.1.2',
      pageTitle: 'onecrm.gui_billing.taxOverview.pageTitle',
    },
  },
  {
    path: BillingManagementRoute.DetailList,
    exact: true,
    component: React.lazy(() => import('views/pages/EBill/DetailList/DetailList')),
    commonPageData: {
      ...defaultCommonPageData,
      pageId: '2.2.1.1.3',
      pageTitle: 'onecrm.gui_billing.detailedStatementList.pageTitle',
    },
  },
  {
    path: BillingManagementRoute.DetailRecords,
    exact: true,
    component: React.lazy(() => import('views/pages/EBill/DetailRecords/DetailRecords')),
    commonPageData: {
      ...defaultCommonPageData,
      pageId: '2.2.1.1.3a',
      pageTitle: 'onecrm gui_billing.detailedStatementItems.pageTitle',
    },
  },
  {
    path: BillingManagementRoute.ClaimsWizard,
    exact: false,
    component: React.lazy(() => import('views/pages/EBill/Claims/ClaimStepsContainer/ClaimSteps')),
    commonPageData: {
      ...defaultCommonPageData,
      // can be anything, but has to be filtered out in routes.ts, you can never actually go to this WF, instead you go to the child routes.
      pageId: '0.0.0',
      pageTitle: 'onecrm.gui_billing.monthStatementClaim.pageTitle',
    },
    children: [...BillingManagementClaimsStepsRoutes],
  },
  {
    path: BillingManagementRoute.PaymentDetail,
    exact: true,
    component: React.lazy(() => import('views/pages/Billing/PaymentDetail/PaymentDetail')),
    commonPageData: {
      ...defaultCommonPageData,
      pageId: '2.2.1.8',
      pageTitle: 'onecrm.gui_billing.paymentDetail.pageTitle',
    },
  },
  {
    path: BillingManagementRoute.DepositDetail,
    exact: true,
    component: React.lazy(() => import('views/pages/Billing/Detail/FinancialDocumentDetail')),
    commonPageData: {
      ...defaultCommonPageData,
      pageId: '2.2.1.5',
    },
  },
  {
    path: BillingManagementRoute.DebitDetail,
    exact: true,
    component: React.lazy(() => import('views/pages/Billing/Detail/FinancialDocumentDetail')),
    commonPageData: {
      ...defaultCommonPageData,
      pageId: '2.2.1.3',
    },
  },
  {
    path: BillingManagementRoute.InvoiceDetail,
    exact: true,
    component: React.lazy(() => import('views/pages/Billing/Detail/FinancialDocumentDetail')),
    commonPageData: {
      ...defaultCommonPageData,
      pageId: '2.2.1.2',
    },
  },
  {
    path: BillingManagementRoute.PenaltyDetail,
    exact: true,
    component: React.lazy(() => import('views/pages/Billing/Detail/FinancialDocumentDetail')),
    commonPageData: {
      ...defaultCommonPageData,
      pageId: '2.2.1.6',
    },
  },
  {
    path: BillingManagementRoute.CreditDetail,
    exact: true,
    component: React.lazy(() => import('views/pages/Billing/Detail/FinancialDocumentDetail')),
    commonPageData: {
      ...defaultCommonPageData,
      pageId: '2.2.1.4',
    },
  },
  {
    path: BillingManagementRoute.ProformaInvoiceDetail,
    exact: true,
    component: React.lazy(() => import('views/pages/Billing/Detail/FinancialDocumentDetail')),
    commonPageData: {
      ...defaultCommonPageData,
      pageId: '2.2.1.10',
    },
  },
  {
    path: BillingManagementRoute.BillingDetail,
    exact: true,
    component: React.lazy(() => import('views/pages/Billing/Detail/FinancialDocumentDetail')),
    commonPageData: {
      ...defaultCommonPageData,
      pageId: '2.2.1.1',
    },
  },
  {
    path: BillingManagementRoute.BillsToPay,
    exact: false,
    component: React.lazy(() => import('views/pages/Billing/InvocesToPay/2.2.1.1.1.4_BillsToPay/BillsToPay')),
    commonPageData: {
      ...defaultCommonPageData,
      pageId: '2.2.1.1.1.4',
    },
  },
];
